import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  designatedInstitutionsApi,
  AgencyType,
  DesignatedInstitution,
  ScheduleInfo,
  SyncLogSummary,
} from '../../lib/designated-institutions';
import { Button, Card } from '@safetyhub/ui';
import { useToastStore } from '../../stores/toast';

const AGENCY_TYPE_LABELS: Record<AgencyType, string> = {
  CONSTRUCTION_GUIDANCE: '건설재해예방기술지도기관',
  SAFETY_MANAGEMENT: '안전관리전문기관',
  HEALTH_MANAGEMENT: '보건관리전문기관',
};

const ALL_AGENCY_TYPES: AgencyType[] = [
  'CONSTRUCTION_GUIDANCE',
  'SAFETY_MANAGEMENT',
  'HEALTH_MANAGEMENT',
];

const PAGE_SIZE_OPTIONS = [50, 100, 200];

const SYNC_STATUS_LABELS: Record<string, string> = {
  SUCCESS: '정상',
  FAILED: '동기화 실패',
  PARTIAL: '부분 성공',
};

const SYNC_STATUS_CLASSES: Record<string, string> = {
  SUCCESS: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
  PARTIAL: 'bg-yellow-100 text-yellow-700',
};

function formatLocalDateTime(isoString: string | null): string {
  if (!isoString) return '동기화 이력 없음';
  const d = new Date(isoString);
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}초`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return sec > 0 ? `${min}분 ${sec}초` : `${min}분`;
}

function SyncIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  );
}

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}


function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function formatNextRunAt(isoString: string): string {
  const d = new Date(isoString);
  const month = d.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' });
  const time = d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${month} ${time}`;
}

function ScheduleBadge({ schedule }: { schedule: ScheduleInfo | null }) {
  return (
    <div className="flex items-center gap-1 text-xs text-gray-500 mt-2">
      <ClockIcon className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
      {schedule ? (
        <span>
          {schedule.description} · 다음: {formatNextRunAt(schedule.nextRunAt)}
        </span>
      ) : (
        <span>수동 동기화</span>
      )}
    </div>
  );
}

function SyncStatusBadge({ lastSync }: { lastSync: SyncLogSummary | null }) {
  if (!lastSync) {
    return (
      <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
        미실행
      </span>
    );
  }
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${SYNC_STATUS_CLASSES[lastSync.status]}`}
    >
      {SYNC_STATUS_LABELS[lastSync.status]}
    </span>
  );
}

// 오류 메시지 펼침 컴포넌트
function ErrorDetail({ message }: { message: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mt-1">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="text-xs text-red-500 underline hover:text-red-700"
      >
        {expanded ? '오류 접기' : '오류 자세히 보기'}
      </button>
      {expanded && (
        <div className="mt-1 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 break-all">
          {message}
        </div>
      )}
    </div>
  );
}


export default function DesignatedInstitutionsTab() {
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();

  const [filterType, setFilterType] = useState<AgencyType | ''>('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [syncingType, setSyncingType] = useState<AgencyType | 'all' | null>(null);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [search]);

  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: [
      'admin',
      'designated-institutions',
      { type: filterType || undefined, search: debouncedSearch || undefined, page, limit },
    ],
    queryFn: () =>
      designatedInstitutionsApi.list({
        type: filterType || undefined,
        search: debouncedSearch || undefined,
        page,
        limit,
      }),
  });

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['admin', 'designated-institutions', 'stats'],
    queryFn: () => designatedInstitutionsApi.getStats(),
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'designated-institutions', 'stats'] });
    queryClient.invalidateQueries({
      queryKey: ['admin', 'designated-institutions', { type: filterType || undefined, search: debouncedSearch || undefined, page, limit }],
    });
  };

  const syncOneMutation = useMutation({
    mutationFn: (type: AgencyType) => designatedInstitutionsApi.syncOne(type),
    onMutate: (type) => setSyncingType(type),
    onSuccess: (result) => {
      setSyncingType(null);
      invalidateAll();
      if (result.error) {
        showToast(`동기화 오류: ${result.error}`, 'error');
      } else {
        showToast(
          `${AGENCY_TYPE_LABELS[result.type]} 동기화 완료 (${result.count}건)`,
          'success',
        );
      }
    },
    onError: (err: Error) => {
      setSyncingType(null);
      showToast(`동기화 실패: ${err.message}`, 'error');
    },
  });

  const syncAllMutation = useMutation({
    mutationFn: () => designatedInstitutionsApi.syncAll(),
    onMutate: () => setSyncingType('all'),
    onSuccess: (results) => {
      setSyncingType(null);
      invalidateAll();
      const failed = results.filter((r) => r.error);
      if (failed.length > 0) {
        showToast(`일부 동기화 실패: ${failed.map((f) => f.error).join(', ')}`, 'error');
      } else {
        const total = results.reduce((sum, r) => sum + r.count, 0);
        showToast(`전체 동기화 완료 (총 ${total}건)`, 'success');
      }
    },
    onError: (err: Error) => {
      setSyncingType(null);
      showToast(`전체 동기화 실패: ${err.message}`, 'error');
    },
  });

  const isSyncing = syncingType !== null;
  const institutions: DesignatedInstitution[] = listData?.data ?? [];
  const meta = listData?.meta ?? { page: 1, limit, total: 0 };
  const totalPages = Math.ceil(meta.total / meta.limit);

  return (
    <div>
      {/* 상단 액션 버튼 */}
      <div className="flex justify-end gap-2 mb-4">
        <Button variant="primary" onClick={() => syncAllMutation.mutate()} disabled={isSyncing}>
          {syncingType === 'all' ? (
            <SpinnerIcon className="w-4 h-4 animate-spin" />
          ) : (
            <SyncIcon className="w-4 h-4" />
          )}
          전체 동기화
        </Button>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {ALL_AGENCY_TYPES.map((type) => {
          const stat = stats?.categories[type];
          const lastSync = stat?.lastSync ?? null;
          const isFailed = lastSync?.status === 'FAILED';
          const isThisSyncing = syncingType === type;

          return (
            <Card key={type} padding="lg" radius="lg">
              {/* 카드 헤더: 기관명 + 상태 배지 */}
              <div className="flex items-start justify-between mb-3">
                <p className="text-sm font-medium text-gray-700 leading-snug pr-2">
                  {AGENCY_TYPE_LABELS[type]}
                </p>
                {statsLoading ? (
                  <div className="h-5 w-14 bg-gray-200 rounded animate-pulse" />
                ) : (
                  <div className="flex items-center gap-1.5">
                    {(stat?.recentFailureCount ?? 0) > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-semibold">
                        {(stat?.recentFailureCount ?? 0) > 99 ? '99+' : stat?.recentFailureCount}
                      </span>
                    )}
                    <SyncStatusBadge lastSync={lastSync} />
                  </div>
                )}
              </div>

              {/* 건수 */}
              {statsLoading ? (
                <div className="h-8 w-20 bg-gray-200 rounded animate-pulse mb-2" />
              ) : (
                <p className="text-3xl font-bold text-gray-900 mb-1">
                  {stat?.count?.toLocaleString() ?? 0}
                  <span className="text-base font-normal text-gray-500 ml-1">건</span>
                  {isFailed && (
                    <span className="text-sm font-normal text-gray-400 ml-1">(이전 데이터)</span>
                  )}
                </p>
              )}

              {/* 동기화 정보 */}
              {!statsLoading && (
                <div className="text-xs text-gray-500 space-y-0.5 mb-3">
                  {lastSync ? (
                    <>
                      <p>
                        {isFailed ? '시도: ' : '마지막 동기화: '}
                        <span className={isFailed ? 'text-red-500' : ''}>
                          {formatLocalDateTime(lastSync.finishedAt)}
                        </span>
                      </p>
                      {!isFailed && (
                        <p className="text-gray-400">소요 시간: {formatDuration(lastSync.durationMs)}</p>
                      )}
                      {isFailed && lastSync.errorMessage && (
                        <>
                          <p className="text-red-500 truncate" title={lastSync.errorMessage}>
                            오류: {lastSync.errorMessage}
                          </p>
                          <ErrorDetail message={lastSync.errorMessage} />
                        </>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-400">동기화 이력 없음</p>
                  )}
                </div>
              )}

              {/* 동기화 버튼 */}
              <button
                onClick={() => syncOneMutation.mutate(type)}
                disabled={isSyncing}
                title={`${AGENCY_TYPE_LABELS[type]} 동기화`}
                className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  isFailed
                    ? 'bg-red-50 hover:bg-red-100 text-red-700 border border-red-200'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                {isThisSyncing ? (
                  <SpinnerIcon className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <SyncIcon className="w-3.5 h-3.5" />
                )}
                {isFailed ? '재동기화' : '동기화'}
              </button>

              {/* 실행 주기 */}
              {!statsLoading && <ScheduleBadge schedule={stats?.schedule ?? null} />}
            </Card>
          );
        })}
      </div>

      {/* 필터 / 검색 */}
      <Card radius="lg" className="mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <select
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value as AgencyType | '');
              setPage(1);
            }}
            className="border border-gray-300 rounded-control px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="">전체 카테고리</option>
            {ALL_AGENCY_TYPES.map((t) => (
              <option key={t} value={t}>
                {AGENCY_TYPE_LABELS[t]}
              </option>
            ))}
          </select>

          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="기관명, 주소 검색..."
              className="w-full border border-gray-300 rounded-control px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            className="border border-gray-300 rounded-control px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {PAGE_SIZE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}개씩
              </option>
            ))}
          </select>

          <span className="text-sm text-gray-500">
            총 {meta.total.toLocaleString()}건
          </span>
        </div>
      </Card>

      {/* 테이블 */}
      <Card padding="none" radius="lg" className="overflow-hidden">
        {listLoading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <SpinnerIcon className="w-6 h-6 animate-spin mr-2" />
            불러오는 중...
          </div>
        ) : institutions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <p className="mb-4">데이터가 없습니다. 동기화를 실행하세요.</p>
            <Button
              variant="primary"
              onClick={() => syncAllMutation.mutate()}
              disabled={isSyncing}
            >
              {syncingType === 'all' ? (
                <SpinnerIcon className="w-4 h-4 animate-spin" />
              ) : (
                <SyncIcon className="w-4 h-4" />
              )}
              전체 동기화
            </Button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">기관명</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">카테고리</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">지정관서</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">전화번호</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">주소</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">공사업종</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">마지막 동기화</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {institutions.map((inst) => (
                    <tr key={inst.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                        {inst.name}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        <span className="inline-block px-2 py-0.5 bg-brand-50 text-brand-700 rounded text-xs">
                          {AGENCY_TYPE_LABELS[inst.agencyType]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {inst.designationOffice ?? '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {inst.phone ?? '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 max-w-xs truncate">
                        {inst.address ?? '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 max-w-xs truncate">
                        {inst.serviceScope ?? '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-400 whitespace-nowrap text-xs">
                        {formatLocalDateTime(inst.lastSyncedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  이전
                </button>
                <span className="text-sm text-gray-600">
                  {page} / {totalPages} 페이지
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  다음
                </button>
              </div>
            )}
          </>
        )}
      </Card>

    </div>
  );
}
